// React imports
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAuth } from "auth/Auth";

// MUI core imports
import { createFilterOptions } from "@mui/material/Autocomplete";

// MUI imports
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

// MUI Icons imports
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SyncIcon from '@mui/icons-material/Sync';
import LockIcon from '@mui/icons-material/Lock';

// MUI Date Picker imports
// DesktopDatePicker keeps the text field editable on touch devices, where the
// responsive DatePicker would fall back to a read-only, calendar-only input.
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Other imports
import AddressAutocompleteInput from 'layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput';
import PropTypes from "prop-types";
import { differenceInYears } from "date-fns";
import { useCountries } from "hooks/useCountries"
import { useCountryCodes } from "hooks/useCountryCodes";
import PrimaryAccountSelector from "./PrimaryAccountSelector";
import TravelerContactSelector from "./TravelerContactSelector";
import {
  createEmptyTraveler,
  GENDER_OPTIONS,
  SPECIAL_DETAILS_MAX_LENGTH,
} from "./hardcodedData";

const DATE_INPUT_FORMAT = "dd/MM/yyyy";

const REQUIRED_FIELDS = [
  "first_name",
  "dateOfBirth",
  "gender",
  "nationality",
  "email",
  "phoneCountryCode",
  "phone",
  "address",
  "country",
];

const FIELD_LABELS = {
  first_name: "First name (as per passport)",
  middle_name: "Middle name (as per passport)",
  last_name: "Last name (as per passport)",
  dateOfBirth: "Date of birth",
  gender: "Gender",
  nationality: "Nationality",
  email: "Email",
  phoneCountryCode: "Phone country code",
  phone: "Phone",
  passportNumber: "Passport number",
  passportExpiry: "Passport expiry",
  passportIssueDate: "Passport issue date",
  passportLocation: "Passport location",
  address: "Address",
  country: "Country",
};

const TravelerDetailsStep = forwardRef(function TravelerDetailsStep(
    {
      seatsError,
      travelers,
      onTravelersChange,
      maxTravelers,
      isLoadingSeats,
      departure,
      readOnly = false,
      isEditMode = false,
      isStructureLocked = false,
      isAccountContactLocked = false,
    },
    ref
) {

  const isAccountLocked = Boolean(isAccountContactLocked || isStructureLocked || readOnly);
  const [fieldErrors, setFieldErrors] = useState({});
  const { countryCodes, getByDialCode, isValidPhoneNumber } = useCountryCodes();
  const { countries } = useCountries();
  const primaryTraveler = travelers.find((t) => t.isPrimary);
  const primaryAccountId =
      primaryTraveler?.accountMode === "existing" ? primaryTraveler.accountId : null;
  const errorKey = (travelerId, field) => `${travelerId}-${field}`;
  const filterCountryCodeOptions = createFilterOptions({
    stringify: (option) => `${option.dialCode} ${option.country}`,
  });

  // Expand or Contract the Accordion on click.

  const handleAccordionChange = (id) => (_, isExpanded) => {
    updateTraveler(id, { expanded: isExpanded });
  };

  // Updates traveler info.

  const updateTraveler = (id, updates) => {
    const currentTraveler = travelers.find((t) => t.id === id);
    const isThisTravelerCancelled = Boolean(readOnly || currentTraveler?.isCancelled);
    if (isThisTravelerCancelled && !("expanded" in updates && Object.keys(updates).length === 1)) {
      return;
    }
    onTravelersChange(
        travelers.map((traveler) =>
            traveler.id === id ? { ...traveler, ...updates } : traveler
        )
    );
  };

  // Synchronize primary traveler phone, dial code,
  // email and address to selected traveler

  const syncPrimaryTravelerContactDetails = (id) => {
    if (readOnly) return;
    travelers.forEach((traveler) => {
      if (traveler.isPrimary) {
        updateTraveler(id, {
          phone: traveler.phone,
          phoneCountryCode: traveler.phoneCountryCode,
          address: traveler.address,
          country: traveler.country,
        });
      }
    });
  };

  // Remove traveler object with associated filled info.

  const handleRemoveTraveler = (id) => {
    if (readOnly) return;
    // Finds the traveler object.
    const traveler = travelers.find((t) => t.id === id);
    // If traveler not found, is primary, or is cancelled, do nothing.
    if (!traveler || traveler.isPrimary || traveler.isCancelled || travelers.length <= 1) return;
    // Remove the traveler object.
    onTravelersChange(travelers.filter((t) => t.id !== id));
  };

  // Add traveler form if there is still available capacity

  const handleAddTraveler = () => {
    if (readOnly) return;
    if (travelers.length >= maxTravelers) return;
    const traveler_details = createEmptyTraveler(false, true)
    onTravelersChange([...travelers, traveler_details]);
    setTimeout(() => {
      const el = document.getElementById(`traveler-${traveler_details.id}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 1000);
  };

  const toComparableDate = (value) => {
    if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
      return new Date(`1970-01-01T${value}:00`);
    }
    return new Date(value);
  };

  // Validate all fields

  const validate = () => {
    const errors = {};
    let firstMessage = "";
    let firstErrorTravelerId = null;
    let firstErrorFieldId = null;

    if (!travelers?.length) {
      return { isValid: false, message: "Please add at least one traveler." };
    }

    const recordFirstError = (travelerId, field, message) => {
      if (!firstMessage) {
        firstMessage = message;
        firstErrorTravelerId = travelerId;
        firstErrorFieldId = field;
      }
    };

    travelers.forEach((traveler, i) => {
      const isThisTravelerCancelled = Boolean(readOnly || traveler.isCancelled);
      if (isThisTravelerCancelled) return;
      const label = traveler.isPrimary ? "Primary traveler" : `Traveler ${i + 1}`;

      if (traveler.isPrimary && !isAccountLocked) {
        if (!traveler.accountMode) {
          errors[errorKey(traveler.id, "accountMode")] =
              "Select whether to use an existing account or create a new one.";
          recordFirstError(traveler.id, "accountMode", `${label}: Please choose an account option.`);
        } else if (traveler.accountMode === "existing" && !traveler.accountId) {
          errors[errorKey(traveler.id, "accountId")] = "Select an existing account.";
          recordFirstError(traveler.id, "accountId", `${label}: Please select an account.`);
        }
      }

      if (!traveler.isPrimary && !isAccountLocked) {
        if (!traveler.contactMode) {
          errors[errorKey(traveler.id, "contactMode")] =
              "Select whether to use an existing contact or create a new one.";
          recordFirstError(traveler.id, "contactMode", `${label}: Please choose a contact option.`);
        } else if (traveler.contactMode === "existing" && !traveler.contactId) {
          errors[errorKey(traveler.id, "contactId")] = "Select an existing contact.";
          recordFirstError(traveler.id, "contactId", `${label}: Please select a contact.`);
        }
      }

      REQUIRED_FIELDS.forEach((field) => {
        const value = traveler[field];
        const isEmpty = !value || (typeof value === "string" && !value.trim());
        if (isEmpty) {
          errors[errorKey(traveler.id, field)] = true;
          recordFirstError(traveler.id, field, `${label}: ${FIELD_LABELS[field]} is required.`);
        }
      });

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (traveler.email && !emailPattern.test(traveler.email.trim())) {
        errors[errorKey(traveler.id, "email")] = true;
        recordFirstError(traveler.id, "email", `${label}: Please enter a valid email address.`);
      }

      const isValid = isValidPhoneNumber({ dialCode: traveler.phoneCountryCode }, traveler.phone)
      if (!isValid) {
        errors[errorKey(traveler.id, "phone")] = true;
        recordFirstError(traveler.id, "phone", `${label}: Please enter a valid phone number.`);
      }

      if (traveler.passportIssueDate && traveler.passportExpiry) {
        if (toComparableDate(traveler.passportExpiry) <= toComparableDate(traveler.passportIssueDate)) {
          errors[errorKey(traveler.id, "passportExpiry")] =
              "Passport expiry date must come after passport issuing date.";
          recordFirstError(traveler.id, "passportExpiry", `${label}: Passport expiry date must come after passport Issuing date.`);
        }
      }

      if (departure?.return_date && traveler.passportExpiry) {

        const sixMonthsAfterDeparture = new Date(
            new Date(departure.return_date).setMonth(
                new Date(departure.return_date).getMonth() + 6
            )
        )

        if (toComparableDate(traveler.passportExpiry) < toComparableDate(sixMonthsAfterDeparture)) {
          errors[errorKey(traveler.id, "passportExpiry")] =
              "Passport must be valid for at least 6 months from the return date.";
          recordFirstError(
              traveler.id,
              "passportExpiry",
              `${label}: Passport must be valid until at least ${sixMonthsAfterDeparture.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} (6 months after return) ).`
          );
        }
      }

      if (traveler?.passportIssueDate) {
        const pid = new Date(traveler?.passportIssueDate);
        if (isNaN(pid.getTime())) {
          errors[errorKey(traveler.id, "passportIssueDate")] =
              "Please enter a valid date.";
          recordFirstError(
              traveler.id,
              "passportIssueDate",
              `${label}: Please enter a valid date.`
          );
        }
      }

      if (traveler?.passportExpiry) {
        const pid = new Date(traveler?.passportExpiry);
        if (isNaN(pid.getTime())) {
          errors[errorKey(traveler.id, "passportExpiry")] =
              "Please enter a valid date.";
          recordFirstError(
              traveler.id,
              "passportExpiry",
              `${label}: Please enter a valid date.`
          );
        }
      }

      if (traveler?.dateOfBirth) {
        const dob = new Date(traveler?.dateOfBirth);
        if (isNaN(dob.getTime())) {
          errors[errorKey(traveler.id, "dateOfBirth")] =
              "Please enter a valid date.";
          recordFirstError(
              traveler.id,
              "dateOfBirth",
              `${label}: Please enter a valid date.`
          );
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dob.setHours(0, 0, 0, 0);

        if (dob > today) {
            errors[errorKey(traveler.id, "dateOfBirth")] =
                "Date of birth can't be in the future.";
            recordFirstError(
                traveler.id,
                "dateOfBirth",
                `${label}: Date of birth can't be in the future.`
            );
          }
        }

      if (traveler.isPrimary && traveler.dateOfBirth) {
        const age = differenceInYears(new Date(), new Date(traveler.dateOfBirth));

        if (age < 18) {
          errors[errorKey(traveler.id, "dateOfBirth")] =
              "Primary traveler must be at least 18 years old.";

          recordFirstError(
              traveler.id,
              "dateOfBirth",
              `${label}: Primary traveler must be at least 18 years old.`
          );
        }
      }

    });

    setFieldErrors(errors);

    // Auto-expand the accordion for the traveler with the first error,
    // so the scroll target below is actually visible.
    if (firstErrorTravelerId) {
      onTravelersChange(
          travelers.map((t) =>
              t.id === firstErrorTravelerId ? { ...t, expanded: true } : t
          )
      );
    }

    return {
      isValid: Object.keys(errors).length === 0,
      message: firstMessage,
      firstErrorFieldDomId: firstErrorTravelerId
          ? `field-${firstErrorTravelerId}-${firstErrorFieldId}`
          : null,
    };
  };

  const showFieldError = (travelerId, field, message) => {
    setFieldErrors((prev) => ({ ...prev, [errorKey(travelerId, field)]: message }));
    onTravelersChange(
        travelers.map((t) => (t.id === travelerId ? { ...t, expanded: true } : t))
    );
    setTimeout(() => {
      document
          .getElementById(`field-${travelerId}-${field}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  // Several server-side errors at once (expands every affected traveler, scrolls to the first one).
  const showFieldErrors = (list) => {
    if (!list.length) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      list.forEach((e) => { next[errorKey(e.travelerId, e.field)] = e.message; });
      return next;
    });
    const ids = new Set(list.map((e) => e.travelerId));
    onTravelersChange(travelers.map((t) => (ids.has(t.id) ? { ...t, expanded: true } : t)));
    setTimeout(() => {
      document
          .getElementById(`field-${list[0].travelerId}-${list[0].field}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  useImperativeHandle(ref, () => ({ validate, showFieldError, showFieldErrors }));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MDBox>

        {/* Header */}

        <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
          Travelers
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={2}>
          {isStructureLocked
              ? "Review and update traveler details."
              : isLoadingSeats
              ? "Checking seat availability..."
              : `Enter traveler details. You can add up to ${maxTravelers} traveler${maxTravelers === 1 ? "" : "s"}.`}
        </MDTypography>
        {seatsError && (
            <MDTypography variant="caption" color="error" display="block" mb={2}>
              {seatsError}
            </MDTypography>
        )}

        {/* Accordion */}

        {travelers.map((traveler, index) => {
          const isThisTravelerCancelled = Boolean(readOnly || traveler.isCancelled);
          return (
          <Accordion
            key={traveler.id}
            expanded={traveler.expanded}
            onChange={handleAccordionChange(traveler.id)}
            disableGutters
            elevation={0}
            sx={{
              mb: 1.5,
              border: "1px solid #E8EDF5",
              borderRadius: "12px !important",
              overflow: "hidden",
              "&:before": { display: "none" },
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                py: 0.5,
                bgcolor: traveler.isPrimary ? "#F0F7FF" : "#FAFBFD",
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  my: 1,
                },
              }}
            >

              {/* Accordion Header */}

              <MDBox id={`traveler-${traveler.id}`} display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                <MDTypography variant="button" fontWeight="bold" noWrap>
                  Traveler {index + 1}
                  {traveler.isPrimary ? " : Primary Traveler" : ""}
                </MDTypography>
                {traveler.isPrimary && (
                  <Chip
                    label="Primary"
                    size="small"
                    sx={{
                      bgcolor: "#E3F2FD",
                      color: "#1565C8",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 22,
                    }}
                  />
                )}
                {isThisTravelerCancelled && (
                  <Chip
                    label="Cancelled"
                    size="small"
                    sx={{
                      bgcolor: "#FFEBEE",
                      color: "#C62828",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      height: 22,
                    }}
                  />
                )}
                {!traveler.isPrimary && traveler.first_name?.trim() && (
                    <MDTypography variant="button" fontWeight="bold" noWrap>
                      : {traveler.first_name}
                    </MDTypography>
                )}
              </MDBox>

              {/* Accordion Header : IF NOT primary and NOT cancelled --> Sync Icon  */}

              {!isThisTravelerCancelled && !traveler.isPrimary && (
                  <MDButton
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={<SyncIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        syncPrimaryTravelerContactDetails(traveler.id);
                      }}
                      sx={{ mr: 0.5 }}
                  >
                    Sync Contact Details
                  </MDButton>
              )}

              {/* Accordion Header : IF NOT primary and NOT cancelled --> Delete Icon  */}

              {!isStructureLocked && !isThisTravelerCancelled && !traveler.isPrimary && (
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove traveler"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTraveler(traveler.id);
                  }}
                  sx={{ mr: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </AccordionSummary>

            {/* Traveler Form */}

            <AccordionDetails sx={{ px: 2, pb: 2.5, pt: 1 }}>

              {traveler.isPrimary && (
                  <PrimaryAccountSelector
                      traveler={traveler}
                      disabled={isAccountLocked || isThisTravelerCancelled}
                      onChange={(updates) => updateTraveler(traveler.id, updates)}
                      modeError={fieldErrors[`${traveler.id}-accountMode`]}
                      accountError={fieldErrors[`${traveler.id}-accountId`]}
                      onClearErrors={() =>
                          setFieldErrors((prev) => ({
                            ...prev,
                            [`${traveler.id}-accountMode`]: false,
                            [`${traveler.id}-accountId`]: false,
                          }))
                      }
                  />
              )}

              {!traveler.isPrimary && (
                  <TravelerContactSelector
                      traveler={traveler}
                      disabled={isAccountLocked || isThisTravelerCancelled}
                      primaryAccountId={primaryAccountId}
                      takenContactIds={travelers
                          .filter((t) => t.id !== traveler.id && t.contactMode === "existing" && t.contactId)
                          .map((t) => t.contactId)}
                      onChange={(updates) => updateTraveler(traveler.id, updates)}
                      modeError={fieldErrors[`${traveler.id}-contactMode`]}
                      contactError={fieldErrors[`${traveler.id}-contactId`]}
                      onClearErrors={() =>
                          setFieldErrors((prev) => ({
                            ...prev,
                            [`${traveler.id}-contactMode`]: false,
                            [`${traveler.id}-contactId`]: false,
                          }))
                      }
                  />
              )}

                <MDBox display="flex" flexWrap="wrap" gap={2}>

                  {/* Input Fields: First Name */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.first_name} required />
                    <TextField
                        id={`field-${traveler.id}-first_name`}
                        fullWidth
                        size="small"
                        disabled={isThisTravelerCancelled}
                        placeholder="First Name"
                        value={traveler.first_name}
                        error={Boolean(fieldErrors[`${traveler.id}-first_name`])}
                        onChange={(e) => {
                          updateTraveler(traveler.id, { first_name: e.target.value });
                          if (fieldErrors[`${traveler.id}-first_name`]) {
                            setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-first_name`]: false }));
                          }
                        }}
                    />
                    {fieldErrors[`${traveler.id}-first_name`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          {typeof fieldErrors[`${traveler.id}-first_name`] === "string"
                              ? fieldErrors[`${traveler.id}-first_name`]
                              : "First name is required."}
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields: Middle Name */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.middle_name} />
                    <TextField
                        fullWidth
                        size="small"
                        disabled={isThisTravelerCancelled}
                        placeholder="Middle Name"
                        value={traveler.middle_name}
                        onChange={(e) =>
                            updateTraveler(traveler.id, { middle_name: e.target.value })
                        }
                    />
                  </MDBox>

                  {/* Input Fields: Last Name */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.last_name} />
                    <TextField
                        fullWidth
                        size="small"
                        disabled={isThisTravelerCancelled}
                        placeholder="Last Name"
                        value={traveler.last_name}
                        onChange={(e) =>
                            updateTraveler(traveler.id, { last_name: e.target.value })
                        }
                    />
                  </MDBox>

                  {/* Input Fields : Date Of Birth */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.dateOfBirth} required />
                    <DesktopDatePicker
                      id={`field-${traveler.id}-dateOfBirth`}
                      disabled={isThisTravelerCancelled}
                      value={traveler.dateOfBirth}
                      format={DATE_INPUT_FORMAT}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: Boolean(fieldErrors[`${traveler.id}-dateOfBirth`]),
                        },
                      }}
                      onChange={(value) => {
                        updateTraveler(traveler.id, { dateOfBirth: value });
                        if (fieldErrors[`${traveler.id}-dateOfBirth`]) {
                          setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-dateOfBirth`]: false }));
                        }
                      }}
                    />
                    {fieldErrors[`${traveler.id}-dateOfBirth`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          {
                            fieldErrors[`${traveler.id}-dateOfBirth`] === true ?
                            "Date of birth is required." :
                            fieldErrors[`${traveler.id}-dateOfBirth`]
                          }
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields : Gender */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.gender} required />
                    <FormControl fullWidth size="small">
                      <Select
                        id={`field-${traveler.id}-gender`}
                        disabled={isThisTravelerCancelled}
                        value={traveler.gender}
                        displayEmpty
                        error={Boolean(fieldErrors[`${traveler.id}-gender`])}
                        onChange={(e) => {
                          updateTraveler(traveler.id, { gender: e.target.value });
                          if (fieldErrors[`${traveler.id}-gender`]) {
                            setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-gender`]: false }));
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>
                        {GENDER_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors[`${traveler.id}-gender`] && (
                          <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                            Gender is required.
                          </MDTypography>
                      )}
                    </FormControl>
                  </MDBox>

                  {/* Input Fields : Nationality */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.nationality} required />
                    <Autocomplete
                        id={`field-${traveler.id}-nationality`}
                        disabled={isThisTravelerCancelled}
                        size="small"
                        options={countries}
                        value={traveler.nationality || null}
                        onChange={(_, value) => {
                          updateTraveler(traveler.id, { nationality: value || "" });

                          if (fieldErrors[`${traveler.id}-nationality`]) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              [`${traveler.id}-nationality`]: false,
                            }));
                          }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select country"
                                error={Boolean(fieldErrors[`${traveler.id}-nationality`])}
                            />
                        )}
                    />
                    {fieldErrors[`${traveler.id}-nationality`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          Nationality is required.
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields : Email */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.email} required />
                    <TextField
                      id={`field-${traveler.id}-email`}
                      fullWidth
                      size="small"
                      disabled={isThisTravelerCancelled}
                      type="email"
                      placeholder="email@example.com"
                      value={traveler.email}
                      error={Boolean(fieldErrors[`${traveler.id}-email`])}
                      onChange={(e) => {
                        updateTraveler(traveler.id, { email: e.target.value });
                        if (fieldErrors[`${traveler.id}-email`]) {
                          setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-email`]: false }));
                        }
                      }}
                    />
                    {fieldErrors[`${traveler.id}-email`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          {/* Email */}
                          {typeof fieldErrors[`${traveler.id}-email`] === "string"
                              ? fieldErrors[`${traveler.id}-email`]
                              : "Valid email is required."}
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields : Phone */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.phone} required />
                    <MDBox display="flex" gap={1}>

                      {/* Country Code */}

                      <Autocomplete
                          id={`field-${traveler.id}-phoneCountryCode`}
                          disabled={isThisTravelerCancelled}
                          size="small"
                          disableClearable
                          options={countryCodes}
                          filterOptions={filterCountryCodeOptions}
                          getOptionLabel={(option) => option.dialCode}
                          value={getByDialCode(traveler?.phoneCountryCode) || null}
                          onChange={(_, value) => {
                            updateTraveler(traveler.id, { phoneCountryCode: value?.dialCode || "" });
                            if (fieldErrors[`${traveler.id}-phone`]) {
                              setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-phone`]: false }));
                            }
                          }}
                          isOptionEqualToValue={(option, value) => option.dialCode === value.dialCode}
                          renderOption={(props, option) => (
                              <MDBox component="li" {...props} key={option.iso2} display="flex" gap={1} sx={{ whiteSpace: "nowrap" }}>
                                <MDTypography variant="caption" color="text">{option.dialCode}</MDTypography>
                                <MDTypography variant="caption" color="text">{option.country}</MDTypography>
                              </MDBox>
                          )}
                          slotProps={{
                            popper: { sx: { width: "260px !important" } },
                          }}
                          sx={{
                            flex: "0 0 30%",
                            minWidth: 0,
                            "& .MuiAutocomplete-input": { minWidth: "30px !important" },
                          }}
                          renderInput={(params) => (
                              <TextField
                                  {...params}
                                  placeholder="+91"
                                  error={Boolean(fieldErrors[`${traveler.id}-phone`])}
                              />
                          )}
                      />

                      {/* Phone Number */}

                      <TextField
                          id={`field-${traveler.id}-phone`}
                          size="small"
                          disabled={isThisTravelerCancelled}
                          placeholder="98765 43210"
                          value={traveler.phone}
                          error={Boolean(fieldErrors[`${traveler.id}-phone`])}
                          onChange={(e) => {
                            updateTraveler(traveler.id, { phone: e.target.value });
                            if (fieldErrors[`${traveler.id}-phone`]) {
                              setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-phone`]: false }));
                            }
                          }}
                          sx={{ flex: "1 1 70%", minWidth: 0 }}
                      />
                    </MDBox>
                    {fieldErrors[`${traveler.id}-phone`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          Valid phone number with country code is required.
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields : Passport Number */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.passportNumber} />
                    <TextField
                      id={`field-${traveler.id}-passportNumber`}
                      fullWidth
                      size="small"
                      disabled={isThisTravelerCancelled}
                      placeholder="Passport number"
                      value={traveler.passportNumber}
                      error={Boolean(fieldErrors[`${traveler.id}-passportNumber`])}
                      onChange={(e) => {
                        updateTraveler(traveler.id, { passportNumber: e.target.value });
                        if (fieldErrors[`${traveler.id}-passportNumber`]) {
                          setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-passportNumber`]: false }));
                        }
                      }}
                    />
                    {fieldErrors[`${traveler.id}-passportNumber`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          Passport Number is required.
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields : Passport Issue Date */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.passportIssueDate} />
                    <DesktopDatePicker
                        id={`field-${traveler.id}-passportIssueDate`}
                        disabled={isThisTravelerCancelled}
                        value={traveler.passportIssueDate}
                        format={DATE_INPUT_FORMAT}
                        onChange={(value) => {
                          updateTraveler(traveler.id, { passportIssueDate: value });
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            error: Boolean(fieldErrors[`${traveler.id}-passportIssueDate`]),
                          },
                        }}
                    />
                  </MDBox>

                  {/* Input Fields : Passport Expiry Date */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.passportExpiry} />
                    <DesktopDatePicker
                      id={`field-${traveler.id}-passportExpiry`}
                      disabled={isThisTravelerCancelled}
                      value={traveler.passportExpiry}
                      format={DATE_INPUT_FORMAT}
                      onChange={(value) => {
                        updateTraveler(traveler.id, { passportExpiry: value });
                        if (fieldErrors[`${traveler.id}-passportExpiry`]) {
                          setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-passportExpiry`]: false }));
                        }
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: Boolean(fieldErrors[`${traveler.id}-passportExpiry`]),
                        },
                      }}
                    />
                    {fieldErrors[`${traveler.id}-passportExpiry`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          {typeof fieldErrors[`${traveler.id}-passportExpiry`] === "boolean"
                              ? "Passport expiry is required."
                              : fieldErrors[`${traveler.id}-passportExpiry`]}
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields: Passport Location */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.passportLocation} />
                    <AddressAutocompleteInput
                        id={`field-${traveler.id}-passportLocation`}
                        placeholder="City / place of issue"
                        value={traveler.passportLocation}
                        onChange={(value) => updateTraveler(traveler.id, { passportLocation: value })}
                        compact
                        disabled={isThisTravelerCancelled}
                    />
                  </MDBox>

                  {/* Input Fields : Country */}

                  <MDBox flex="1 1 220px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.country} required />
                    <Autocomplete
                        id={`field-${traveler.id}-country`}
                        disabled={isThisTravelerCancelled}
                        size="small"
                        options={countries}
                        value={traveler.country || null}
                        error={Boolean(fieldErrors[`${traveler.id}-country`])}
                        onChange={(_, value) => {
                          updateTraveler(traveler.id, { country: value || "" });

                          if (fieldErrors[`${traveler.id}-country`]) {
                            setFieldErrors((prev) => ({
                              ...prev,
                              [`${traveler.id}-country`]: false,
                            }));
                          }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select country"
                                error={Boolean(fieldErrors[`${traveler.id}-country`])}
                            />
                        )}
                    />
                    {fieldErrors[`${traveler.id}-country`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          Country is required.
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields: Address */}

                  <MDBox flex="1 1 440px" minWidth={200}>
                    <FieldLabel label={FIELD_LABELS.address} required />
                    <AddressAutocompleteInput
                        id={`field-${traveler.id}-address`}
                        placeholder="Address"
                        value={traveler.address}
                        onChange={(value) => {
                          updateTraveler(traveler.id, { address: value });
                          if (fieldErrors[`${traveler.id}-address`]) {
                            setFieldErrors((prev) => ({ ...prev, [`${traveler.id}-address`]: false }));
                          }
                        }}
                        error={Boolean(fieldErrors[`${traveler.id}-address`])}
                        required
                        compact
                        disabled={isThisTravelerCancelled}
                    />
                    {fieldErrors[`${traveler.id}-address`] && (
                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                          Address is required.
                        </MDTypography>
                    )}
                  </MDBox>

                  {/* Input Fields : Special Detail Field */}

                  <MDBox flex="1 1 100%">
                    <FieldLabel
                      label="Special details"
                      hint="Dietary preferences, wheelchair assistance, meal requests, etc."
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      disabled={isThisTravelerCancelled}
                      placeholder="E.g. vegetarian meal, nut allergy, wheelchair assistance, early check-in..."
                      value={traveler.specialDetails}
                      onChange={(e) => {
                        const value = e.target.value.slice(0, SPECIAL_DETAILS_MAX_LENGTH);
                        updateTraveler(traveler.id, { specialDetails: value });
                      }}
                      helperText={`${traveler.specialDetails.length}/${SPECIAL_DETAILS_MAX_LENGTH} characters`}
                    />
                  </MDBox>

              </MDBox>
            </AccordionDetails>
          </Accordion>
        );
        })}

        {/* Add Traveler Button / Locked Message */}

        {isStructureLocked ? (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            py={2}
            px={3}
            mt={2}
            sx={{
              width: "100%",
              borderRadius: "8px",
              bgcolor: "#F8FAFC",
              border: "1px dashed #CBD5E1",
            }}
          >
            <LockIcon sx={{ fontSize: 18, color: "#64748B" }} />
            <MDTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.85rem", color: "#64748B", textAlign: "center" }}>
              You can&apos;t add more travelers as invoices have been generated for this travel order.
            </MDTypography>
          </MDBox>
        ) : (
          !readOnly && (
            <MDButton
              variant="outlined"
              color="info"
              startIcon={<PersonAddAltIcon />}
              onClick={handleAddTraveler}
              disabled={travelers.length >= maxTravelers}
              sx={{ mt: 1 }}
            >
              Add Another Traveler
            </MDButton>
          )
        )}

        {!isStructureLocked && !readOnly && travelers.length >= maxTravelers && (
            <MDTypography variant="caption" color="error" display="block" mt={1}>
              Maximum of {maxTravelers} travelers reached based on available seats.
            </MDTypography>
        )}
      </MDBox>
    </LocalizationProvider>
  );
});

function FieldLabel({ label, required, hint }) {
  return (
    <Box mb={0.5}>
      <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
        {label}
        {required && (
          <Box component="span" sx={{ color: "#E53935", ml: 0.25 }}>
            *
          </Box>
        )}
      </MDTypography>
      {hint && (
        <MDTypography variant="caption" color="text" opacity={0.8}>
          {hint}
        </MDTypography>
      )}
    </Box>
  );
}


TravelerDetailsStep.propTypes = {
  readOnly: PropTypes.bool,
  isEditMode: PropTypes.bool,
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isPrimary: PropTypes.bool,
      fullName: PropTypes.string,
      dateOfBirth: PropTypes.instanceOf(Date),
      gender: PropTypes.string,
      nationality: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      passportNumber: PropTypes.string,
      passportExpiry: PropTypes.instanceOf(Date),
      specialDetails: PropTypes.string,
      expanded: PropTypes.bool,
      country: PropTypes.string,
      address: PropTypes.string,
    })
  ).isRequired,
  onTravelersChange: PropTypes.func.isRequired,
  maxTravelers: PropTypes.number,
  isLoadingSeats: PropTypes.bool,
  seatsError: PropTypes.string,
  departure: PropTypes.shape({
    return_date: PropTypes.string,
  }),
  isStructureLocked: PropTypes.bool,
  isAccountContactLocked: PropTypes.bool,
};

FieldLabel.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  hint: PropTypes.string,
};

export default TravelerDetailsStep;
